import {
  call, debounce,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import {
  requestBoundaryItems, waitForBoundaryGroups,
} from '~/store/boundaryItems/boundaryItems.sagas';
import { boundaryTerritoryGroupsFetchBBOXDetailsRequest } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.actionCreators';
import {
  requestPremiumDemographicsIfNeeded, waitForPremiumDemographicsReady,
} from '~/store/demographics/premiumDemographics/demographicsListPremium.sagas';
import { dataLayersToolUpdateRequest } from '~/store/frontendState/mapTools/dataLayers/dataLayers.actionCreators';
import { isDataLayerToolActiveSelector } from '~/store/frontendState/mapTools/dataLayers/dataLayers.selectors';
import { mapSettingsActiveDataLayerGroupSelector } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import { areMapSettingsReadySelector } from '~/store/mapSettings/ready/mapSettingsReady.selectors';
import { activeToolChanged } from '~/store/mapSettings/toolsState/activeTool/mapSettingsToolsStateActiveTool.actionCreators';
import { MapSettingsToolsStateActiveTool } from '~/store/mapSettings/toolsState/activeTool/mapSettingsToolsStateActiveTool.state';
import { BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS } from '../boundaryTerritoryGroups.actionTypes';
import { dataLayersBTGsSelector } from './dataLayersBTGs.selectors';

export function* dataLayersToolBoundaryTerritoryGroupSagas() {
  yield debounce(
    1000,
    activeToolChanged,
    requestBoundaryDataOnActiveToolChange // The displayed boundaries depend on the active tool, so they are reloaded whenever it changes
  );
  yield takeLatest([
    activeToolChanged,
    BOUNDARY_TERRITORY_GROUP_FETCH_SUCCESS, // fetching the BTGs replaces all of them, including the Data Layers ones
  ], ensureDataLayersBTGsExist);
}

function* requestBoundaryDataOnActiveToolChange(action: ReturnType<typeof activeToolChanged>) {
  if (action.payload.newTool === MapSettingsToolsStateActiveTool.DataLayers) {
    // the tool can be active without its panel being opened, so the list has to be requested here as well
    yield call(requestPremiumDemographicsIfNeeded);
    // the boundaries of the Data Layers tool depend on the list, so wait for it even if it is still loading
    yield call(waitForPremiumDemographicsReady);
  }

  yield call(requestBoundaryItems);
  yield put(boundaryTerritoryGroupsFetchBBOXDetailsRequest());
}

// The Data Layers BTGs stay in the store while the other tool is active, so they are created only when they are missing:
// when the tool gets active for the first time, or when they were lost (they exist only on the FE).
// Changes of the data layers themselves are handled by onDataLayersChanged.
function* ensureDataLayersBTGsExist() {
  const areMissing: boolean = yield call(areDataLayersBTGsMissing);

  if (!areMissing) {
    return;
  }

  yield call(waitForPremiumDemographicsReady);
  yield call(waitForBoundaryGroups, { waitForBTGs: false });

  // something else could have created them while waiting
  const areStillMissing: boolean = yield call(areDataLayersBTGsMissing);

  if (areStillMissing) {
    yield put(dataLayersToolUpdateRequest());
  }
}

function* areDataLayersBTGsMissing() {
  const areMapSettingsReady: ReturnType<typeof areMapSettingsReadySelector> = yield select(areMapSettingsReadySelector);
  const isDataLayerToolActive: ReturnType<typeof isDataLayerToolActiveSelector> = yield select(isDataLayerToolActiveSelector);
  const activeDataLayerGroup: ReturnType<typeof mapSettingsActiveDataLayerGroupSelector> = yield select(mapSettingsActiveDataLayerGroupSelector);
  const dataLayersBTGs: ReturnType<typeof dataLayersBTGsSelector> = yield select(dataLayersBTGsSelector);

  return areMapSettingsReady && isDataLayerToolActive && !!activeDataLayerGroup && dataLayersBTGs.length === 0;
}
